import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatDay } from "../lib/finance";
import { hasPermission } from "../lib/permissions";
import {
  ACTION_LABELS, END_CODE_LABELS, PERIODICITY_LABELS, billingError, money, newerPrice, priceText,
} from "../lib/billing";
import { formatDateTime } from "../lib/tickets";
import { Field, SubscriptionStatusBadge } from "../components/billing/BillingBits";
import { ChangeOfferDialog, EditDialog, EndDialog, ReasonDialog, StartDialog } from "../components/billing/BillingDialogs";
import { DocumentsList, InstallmentsList, PaymentsList } from "../components/billing/BillingTables";
import useBillingActions from "../components/billing/useBillingActions";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { SubscriptionContractCard } from "../components/records/RelatedRecords";
import { Skeleton } from "../components/ui/skeleton";

// One line of the subscription's history, in words (amounts and reasons are shown here, never in the general journal).
function historyText(h, currency) {
  const d = h.data || {};
  const amount = (v) => (v != null ? money(v, d.currency || currency) : "");
  switch (h.kind) {
    case "created": return `Créé : ${d.offer} v${d.version_number}, ${PERIODICITY_LABELS[d.periodicity]?.toLowerCase()} à ${amount(d.price_minor)}${d.price_reason ? ` (${d.price_reason})` : ""}${d.replaces ? `, remplace ${d.replaces}` : ""}`;
    case "updated": if (d.version_number) return `Passé à la version ${d.version_number} de l'offre (était v${d.previous_version_number}), ${PERIODICITY_LABELS[d.periodicity]?.toLowerCase()} à ${amount(d.price_minor)}`;
      return d.price_minor != null ? `Prix : ${amount(d.previous_price_minor)} → ${amount(d.price_minor)}${d.price_reason ? ` (${d.price_reason})` : ""}` : "Modifié";
    case "start": return d.to === "trial" ? "Essai démarré" : `Démarré, facturé à partir du ${formatDay(d.billing_anchor)}`;
    case "start_billing": return `Facturation démarrée le ${formatDay(d.billing_anchor)}`;
    case "pause": return `Mis en pause : ${d.reason}`;
    case "resume": return "Repris";
    case "end": return `Terminé le ${formatDay(d.end_date)} (${END_CODE_LABELS[d.end_code] || ""}) : ${d.reason}`;
    case "cancel": return `Brouillon annulé${d.reason ? ` : ${d.reason}` : ""}`;
    case "offer_changed": return `Offre changée (dernier jour ${formatDay(d.end_date)}), remplacé par ${d.replaced_by} : ${d.reason}`;
    case "installment_generated": return `Échéance du ${formatDay(d.period_start)} créée : ${amount(d.amount_minor)}, à payer avant le ${formatDay(d.due_date)}`;
    default: return h.kind;
  }
}

export default function BillingSubscription() {
  const { subscriptionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [offers, setOffers] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/billing/subscriptions/${subscriptionId}`);
      setSub(data);
      setPhase("ready");
    } catch (e) {
      setError(billingError(e, "Impossible de charger cet abonnement."));
      setPhase("error");
    }
  }, [subscriptionId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);
  const rows = useBillingActions(user, load);
  // A change of offer answers with the NEW subscription: the page moves to it.
  const done = (data) => { setDialog(null); if (data?.id && data.id !== subscriptionId) navigate(`/revenus/abonnements/${data.id}`); else load(); };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{error}</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }
  const caps = sub.capabilities || {};
  const openAction = async (action) => {
    if (action === "change_offer") {
      const { data } = await api.get("/billing/offers", { params: { include_archived: false } });
      setOffers(data.items);
    }
    setDialog(action);
  };
  // A draft simply takes the version now on sale; a running subscription keeps its contract until someone changes its offer.
  const takeNewerVersion = async () => {
    setUpgrading(true);
    try {
      await api.patch(`/billing/subscriptions/${sub.id}`, { expected_version: sub.version, offer_id: sub.offer.offer_id });
      toast.success(`Brouillon passé à la version ${sub.newer_version.number}`);
      await load();
    } catch (e) {
      toast.error(billingError(e, "Impossible de changer de version. Réessayez."));
    } finally {
      setUpgrading(false);
    }
  };
  const newer = sub.newer_version ? newerPrice(sub) : null;
  const canSeeCustomer = !!user?.features?.customers && hasPermission(user, ["customers:view", "customers:view_all"]);
  const primaryActions = (caps.actions || []).filter((a) => !["cancel", "end"].includes(a));
  const toInvoice = (sub.installments || []).filter((i) => i.state !== "cancelled" && !i.invoice_id && i.remaining_minor > 0);

  return (
    <div className="space-y-4" data-testid="billing-subscription-page">
      <Link to="/revenus/abonnements" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Abonnements clients</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display font-bold text-2xl text-slate-900">{sub.pharmacy_name}</h1>
            <SubscriptionStatusBadge status={sub.status} />
          </div>
          <p className="text-sm text-slate-500 tabular-nums">
            {sub.number} · {sub.offer.offer_name} (version {sub.offer.version_number}) · {priceText(sub.price_minor, sub.currency, sub.periodicity)}
            {canSeeCustomer && <> · <Link to={`/clients/${sub.prospect_id}`} className="text-jamora hover:underline">Dossier client</Link></>}
          </p>
        </div>
        {caps.manage && (
          <div className="flex flex-wrap gap-2" data-testid="billing-subscription-actions">
            {caps.edit && <Button variant="outline" onClick={() => setDialog("edit")} data-testid="billing-action-edit">{caps.edit === "draft" ? "Modifier" : "Modifier le prix"}</Button>}
            {primaryActions.map((a) => (
              <Button key={a} variant={a === "start" || a === "start_billing" ? "default" : "outline"} onClick={() => openAction(a)} data-testid={`billing-action-${a}`}>
                {ACTION_LABELS[a]}
              </Button>
            ))}
            {caps.actions.includes("end") && <Button variant="outline" className="text-red-600" onClick={() => setDialog("end")} data-testid="billing-action-end">Terminer</Button>}
            {caps.actions.includes("cancel") && <Button variant="ghost" className="text-red-600" onClick={() => setDialog("cancel")} data-testid="billing-action-cancel">Annuler le brouillon</Button>}
          </div>
        )}
      </div>

      {newer && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="billing-newer-version">
          <p className="text-sm text-amber-900 min-w-0">
            La version {sub.newer_version.number} de l'offre est en vente : {priceText(newer.price_minor, sub.newer_version.currency, newer.periodicity)}.{" "}
            {sub.status === "draft"
              ? `Ce brouillon est encore sur la version ${sub.offer.version_number}.`
              : `Cet abonnement garde la version ${sub.offer.version_number}, celle de son contrat, tant que vous ne changez pas son offre.`}
          </p>
          {caps.edit === "draft" && (
            <Button size="sm" onClick={takeNewerVersion} disabled={upgrading} data-testid="billing-take-newer">
              {upgrading && <Loader2 size={14} className="mr-1.5 animate-spin" />}Passer à la version {sub.newer_version.number}
            </Button>
          )}
          {caps.actions?.includes("change_offer") && (
            <Button size="sm" variant="outline" onClick={() => openAction("change_offer")} data-testid="billing-newer-change">Changer d'offre</Button>
          )}
        </div>
      )}

      {sub.account && !["active", "suspended"].includes(sub.account.status) && sub.status === "draft" && (
        <p className="text-sm text-amber-700" data-testid="billing-account-not-live">Le dossier client n'est pas encore en service : un essai peut démarrer, la facturation attendra la mise en service.</p>
      )}

      <div className="grid lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <Card className="overflow-hidden" data-testid="billing-installments-card">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4 pb-2">
              <div>
                <h2 className="font-semibold text-slate-900">Échéances</h2>
                <p className="text-xs text-slate-500">Une par période, à partir de celle où la facturation a commencé.</p>
              </div>
              {rows.canInvoice && toInvoice.length > 1 && (
                <Button variant="outline" size="sm" onClick={() => rows.openInvoice(toInvoice)} data-testid="billing-invoice-all">Facturer les {toInvoice.length} échéances</Button>
              )}
            </div>
            {sub.installments.length === 0
              ? <p className="px-4 pb-4 text-sm text-slate-500">{sub.status === "active" ? "La première échéance se créera au début de la première période." : "Aucune échéance tant que la facturation n'a pas démarré."}</p>
              : <InstallmentsList items={sub.installments} actions={rows.installmentActions} />}
          </Card>
          {sub.documents.length > 0 && (
            <Card className="overflow-hidden" data-testid="billing-documents-card">
              <h2 className="font-semibold text-slate-900 px-4 pt-4 pb-1">Factures et avoirs</h2>
              <DocumentsList items={sub.documents} actions={rows.documentActions} />
            </Card>
          )}
          {sub.payments.length > 0 && (
            <Card className="overflow-hidden" data-testid="billing-payments-card">
              <h2 className="font-semibold text-slate-900 px-4 pt-4 pb-1">Encaissements</h2>
              <PaymentsList items={sub.payments} actions={rows.paymentActions} />
            </Card>
          )}
        </div>
        <div className="space-y-4 min-w-0">
          <Card className="p-4">
            <h2 className="font-semibold text-slate-900 mb-1">Contrat</h2>
            <dl className="divide-y divide-slate-100">
              <Field label="Début">{formatDay(sub.start_date)}</Field>
              {sub.trial_end_date && <Field label="Fin de l'essai">{formatDay(sub.trial_end_date)}</Field>}
              {sub.billing_anchor && <Field label="Facturé depuis">{formatDay(sub.billing_anchor)}</Field>}
              <Field label="Prix">
                {priceText(sub.price_minor, sub.currency, sub.periodicity)}
                {sub.negotiated && <span className="block text-xs text-amber-700">Négocié{sub.list_price_minor != null ? ` (offre : ${money(sub.list_price_minor, sub.currency)})` : ""} : {sub.price_reason}</span>}
              </Field>
              <Field label="Par mois">{money(sub.monthly_minor, sub.currency)}</Field>
              {sub.offer.users_included && <Field label="Utilisateurs inclus">{sub.offer.users_included}</Field>}
              {sub.pause_reason && <Field label="En pause">{sub.pause_reason}</Field>}
              {sub.end_date && <Field label="Fin">{formatDay(sub.end_date)} · {END_CODE_LABELS[sub.end_code]}{sub.end_reason ? ` · ${sub.end_reason}` : ""}</Field>}
              {sub.note && <Field label="Note"><span className="whitespace-pre-line">{sub.note}</span></Field>}
            </dl>
          </Card>
          <SubscriptionContractCard subscriptionId={sub.id} />
          <Card className="p-4">
            <h2 className="font-semibold text-slate-900 mb-2">Historique</h2>
            <ol className="space-y-2" data-testid="billing-history">
              {sub.history.slice().reverse().map((h, index) => (
                <li key={index} className="text-sm">
                  <div className="text-slate-800">{historyText(h, sub.currency)}</div>
                  <div className="text-xs text-slate-500">{h.actor_name || "Jamora CRM"} · {formatDateTime(h.created_at)}</div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>

      {(dialog === "start" || dialog === "start_billing") && <StartDialog sub={sub} action={dialog} onOpenChange={() => setDialog(null)} onDone={done} />}
      {dialog === "end" && <EndDialog sub={sub} onOpenChange={() => setDialog(null)} onDone={done} />}
      {dialog === "edit" && <EditDialog sub={sub} onOpenChange={() => setDialog(null)} onDone={done} />}
      {dialog === "change_offer" && <ChangeOfferDialog sub={sub} offers={offers} onOpenChange={() => setDialog(null)} onDone={done} />}
      <ReasonDialog open={dialog === "pause"} title="Mettre l'abonnement en pause ?" confirm="Mettre en pause" success="Abonnement en pause"
        description="Aucune échéance ne sera créée pendant la pause. Le dossier client et Jamora ne sont pas touchés." placeholder="Fermeture pour travaux…"
        onOpenChange={(o) => !o && setDialog(null)} onDone={done}
        onSubmit={(reason) => api.post(`/billing/subscriptions/${sub.id}/pause`, { expected_version: sub.version, reason })} />
      <ReasonDialog open={dialog === "resume"} title="Reprendre l'abonnement ?" confirm="Reprendre" optional label="Remarque" success="Abonnement repris"
        description="La facturation reprend à la période en cours ; les périodes de pause ne sont pas facturées."
        onOpenChange={(o) => !o && setDialog(null)} onDone={done}
        onSubmit={() => api.post(`/billing/subscriptions/${sub.id}/resume`, { expected_version: sub.version })} />
      <ReasonDialog open={dialog === "cancel"} title="Annuler ce brouillon ?" confirm="Annuler le brouillon" danger optional success="Brouillon annulé"
        description="Il reste visible, annulé. Vous pourrez créer un autre abonnement pour cette pharmacie."
        onOpenChange={(o) => !o && setDialog(null)} onDone={done}
        onSubmit={(reason) => api.post(`/billing/subscriptions/${sub.id}/cancel`, { expected_version: sub.version, reason: reason || null })} />
      {rows.dialogs}
    </div>
  );
}
